import react, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, NewSelect, NewModal, MainContentPaddingTypes } from "../..";
import { colors, fonts, glyphs } from "../../../Styles";
// import { FieldTypes, FieldStyles } from "../../../Definitions";
import { useAppSelector } from "../../../Store";
import { Graph_QL } from "../../../Services";
import { useSection } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";
import { useAnimal } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal";
import { useSystem } from "../../../Services/Agrisys_GqlDataCaller/SystemCalls/useSystem";
import { useParams } from "react-router";
import { LfLocation } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall";

const SYSTEM_ID = 1;

interface SkipFeeding_Props{
  isModalOpen: boolean
  goBack: () => void,
  pen?: LfLocation 
}
/**
 * SkipFeeding
 * @param goBack Function to controle on "goBack" btn. 
 * @returns A modal with calendar etc. to make a feed skip.
 */
export default function SkipFeeding({isModalOpen, goBack, pen}:SkipFeeding_Props) {
  const {t} = useTranslation()
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [selectedDateTime, setSelectedDateTime] = useState<string>("");
  const [startTime, setStartTime] = useState<Date>(new Date());
  const [endTime, setEndTime] = useState<Date>(new Date());
  const [selectedTimeFormat, setSelectedTimeFormat] = useState<number>(0);
  const [selectedTimeAmount, setSelectedTimeAmount] = useState<number>(0)
  const { systemId: urlSystemId } = useParams(); // TODO routing changes in order to get a correct system info
  const systemId = urlSystemId ? +urlSystemId : SYSTEM_ID;
  
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  
  const { call: getLfSectionsInfo, data: lfSectionsState } = useSection(connection);
  const { call: getSystemCall, data: systemsState } = useSystem(connection);
  
  const systemName = useMemo(() => 
    systemsState[domain]?.data.find((system) => system.systemId === systemId)?.name, 
    [systemsState, domain, systemId]
  );
  
  const sections = useMemo(() => lfSectionsState[domain]?.data, [domain, lfSectionsState]);
  
  useEffect(() => {
    getLfSectionsInfo({ ids: [systemId] }, connection);
    // eslint-disable-next-line
  }, []);
  
  const findSectionByPenId = useCallback((penId: number) => {
    if (!sections) return null;
    const section = sections.find((section) =>
      section.section_locations.some((location) => location.location_id === penId)
    );
    return section?.name ?? null;
  }, [sections]);
  
  const sectionName = pen ? findSectionByPenId(pen.location_id) : undefined;

  // Find label in select field options corresponding to their respective ID
  const findLabelById = (id: number, options: { label: string, id: number }[]) => {
    const option = options.find(option => option.id === id);
    return option ? option.label : "";
  };

  // Set the starttime to nearest positive quarter hour and the initial end time to 15 minutes after the rounded start time
  useEffect(() => {
    // Get current time and round to the nearest quarter hour
    const now = new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 15) * 15;
  
    if (roundedMinutes === 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0);
    } else {
      now.setMinutes(roundedMinutes);
    }
  
    // Set the initial end time to 15 minutes after the rounded start time
    const initialEndTime = new Date(now);
    initialEndTime.setMinutes(now.getMinutes() + 15);
  
    setStartTime(now);
    setEndTime(initialEndTime);
  }, []);

  // #region | Selected Date and Time Logic
  useEffect(() => {
    const formatDate = (date: Date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    const formatTime = (date: Date) => {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    const _startDate = new Date(startDate);
    _startDate.setHours(0, 0, 0, 0);

    const _endDate = new Date(endDate);
    _endDate.setHours(0, 0, 0, 0);

    const now = new Date();
    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0)
    const currentTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), 0, 0);

    switch (selectedTimeFormat) {
      case 1: // Time Amount
        if (_startDate.getTime() < currentDate.getTime()) {
          setSelectedDateTime(t("feed.feedSkip.invalidDay", {defaultValue: "Invalid Day"}));
        } else if ((_startDate.getTime() === currentDate.getTime() && startTime.getTime() < currentTime.getTime())) {
          setSelectedDateTime(t("feed.feedSkip.invalidTime", {defaultValue: "Invalid Time"}));
        } else {
          setSelectedDateTime(`${formatDate(_startDate)} ${formatTime(startTime)} + ${(findLabelById(selectedTimeAmount, selectedTimeAmountOptions))}`)
        }
      break;
      case 2: // Time Span
      default:
        if (_startDate.getTime() < currentDate.getTime()) {
          setSelectedDateTime(t("feed.feedSkip.invalidDay", {defaultValue: "Invalid Day"}));
        } else if ((_startDate.getTime() === currentDate.getTime() && startTime.getTime() < currentTime.getTime())) {
          setSelectedDateTime(t("feed.feedSkip.invalidTime", {defaultValue: "Invalid Time"}));
        } else if (_endDate.getTime() < _startDate.getTime()) {
          setSelectedDateTime(t("feed.feedSkip.endDateBeforeStartDate", {defaultValue: "The end date occurs before the start date"}));
        } else if (_endDate.getTime() === _startDate.getTime()) {
          if (endTime.getTime() < startTime.getTime()) {
            setSelectedDateTime(t("feed.feedSkip.endTimeBeforeStartTime", {defaultValue: "The end time occurs before the start time"}));
          } else if (startTime.getTime() === endTime.getTime()) {
            setSelectedDateTime(`${formatDate(_startDate)} ${formatTime(startTime)}`);
          } else {
            setSelectedDateTime(`${formatDate(_startDate)} ${formatTime(startTime)} - ${formatTime(endTime)}`);
          }
        } else {
          if (endTime.getTime() < startTime.getTime()) {
            setSelectedDateTime(t("feed.feedSkip.endTimeBeforeStartTime", {defaultValue: "The end time occurs before the start time"}));
          } else {
            setSelectedDateTime(`${formatDate(_startDate)} ${formatTime(startTime)} - ${formatDate(_endDate)} ${formatTime(endTime)}`);
          }
        }
      break;
      }
    }, [startDate, endDate, startTime, endTime, selectedTimeFormat, selectedTimeAmount]);
  // #endregion

  // #region | Time Format and Time Amount options
  // Time Format
  const selectedTimeFormatOptions = [
    {label: t("feed.feedSkip.timeSpan", {defaultValue: "Time Span"}), id: 0},
    {label: t("feed.feedSkip.timeAmount", {defaultValue: "Time Amount"}), id: 1}
  ];

  // Time Amount
  const selectedTimeAmountOptions = [                      
    {label:"1 " + t("units.hour", {defaultValue: "Hour"}), id:0},  
    {label:"2 " + t("units.hours", {defaultValue: "Hours"}), id:1},
    {label:"3 " + t("units.hours", {defaultValue: "Hours"}), id:2},
    {label:"4 " + t("units.hours", {defaultValue: "Hours"}), id:3},
    {label:"5 " + t("units.hours", {defaultValue: "Hours"}), id:4},
    {label:"6 " + t("units.hours", {defaultValue: "Hours"}), id:5},
    {label:"7 " + t("units.hours", {defaultValue: "Hours"}), id:6},
    {label:"8 " + t("units.hours", {defaultValue: "Hours"}), id:7},
    {label:"9 " + t("units.hours", {defaultValue: "Hours"}), id:8},
    {label:"10 " + t("units.hours", {defaultValue: "Hours"}), id:9},
    {label:"11 " + t("units.hours", {defaultValue: "Hours"}), id:10},
    {label:"12 " + t("units.hours", {defaultValue: "Hours"}), id:11},
    {label:"13 " + t("units.hours", {defaultValue: "Hours"}), id:12},
    {label:"14 " + t("units.hours", {defaultValue: "Hours"}), id:13},
    {label:"15 " + t("units.hours", {defaultValue: "Hours"}), id:14},
    {label:"16 " + t("units.hours", {defaultValue: "Hours"}), id:15},
    {label:"17 " + t("units.hours", {defaultValue: "Hours"}), id:16},
    {label:"18 " + t("units.hours", {defaultValue: "Hours"}), id:17},
    {label:"19 " + t("units.hours", {defaultValue: "Hours"}), id:18},
    {label:"20 " + t("units.hours", {defaultValue: "Hours"}), id:19},
    {label:"21 " + t("units.hours", {defaultValue: "Hours"}), id:20},
    {label:"22 " + t("units.hours", {defaultValue: "Hours"}), id:21},
    {label:"23 " + t("units.hours", {defaultValue: "Hours"}), id:22},
    {label:"24 " + t("units.hours", {defaultValue: "Hours"}), id:23}
  ]
  // #endregion

  return (
    <NewModal 
      modalState={isModalOpen}
      header={t("feed.feedSkip.planFeedSkip", {defaultValue: "Plan Feed Skip"})}
      subHeader={ pen ? `${systemName} ${sectionName} Pen ${pen}` : undefined}
      overrides={{ 
        overlayColor: colors.transparent,
        mainContentPadding: MainContentPaddingTypes.secondary
      }}
      predefinedButtons={{
        onGoBack: () => goBack()
      }}
      mainContent={
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "20px"}}>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "end", gap: "40px"}}>
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "start", gap: "15px", height: "155px"}}>
              <NewSelect
                options={selectedTimeFormatOptions}
                onChange={(e) => setSelectedTimeFormat(Number(e?.id ?? 0))}
                valueOverrides={{
                  defaultValue: selectedTimeFormatOptions.find(option => option.id === selectedTimeFormat)
                }}
              />
              <div>
                <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("feed.feedSkip.startDate", {defaultValue: "Start Date"})}</p>
                {/* <DateField value={startDate} onChange={({ date }) => setStartDate(date)} /> */}
              </div>
              {selectedTimeFormat === 0 
                ? <div>
                    <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("feed.feedSkip.endDate", {defaultValue: "End Date"})}</p>
                    {/* <DateField value={endDate} onChange={({ date }) => setEndDate(date)} /> */}
                  </div> 
                : selectedTimeFormat === 1 ? 
                  <div>
                  </div> 
                : null 
              }
            </div>
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "end", gap: "15px", height: "155px"}}>
              <div>
                <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("feed.feedSkip.startTime", {defaultValue: "Start Time"})}</p>
                {/* <TimeField value={startTime} startEnhancer fieldType={FieldTypes.popoverDropdown} fieldStyle={FieldStyles.popoverDropdown} onChange={(date) => setStartTime(date)} /> */}
              </div>
              {selectedTimeFormat === 0 
                ? <div>
                    <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("feed.feedSkip.endTime", {defaultValue: "End Time"})}</p>
                    {/* <TimeField value={endTime} startEnhancer fieldType={FieldTypes.popoverDropdown} fieldStyle={FieldStyles.popoverDropdown} onChange={(date) => setEndTime(date)} /> */}
                  </div>
                : selectedTimeFormat === 1 ?
                  <div>
                    <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}></p>
                    <NewSelect
                      header={t("feed.feedSkip.timeAmount", {defaultValue: "time amount"})}
                      startEnhancer={glyphs.Time({})}               
                      options={selectedTimeAmountOptions}
                      onChange={(e) => setSelectedTimeAmount(Number(e?.id ?? 0))}
                      valueOverrides={{
                        defaultValue: selectedTimeAmountOptions.find(option => option.id === selectedTimeAmount)
                      }}
                    />
                  </div>
                : null
              }
            </div>
          </div>
          <p style={{ ...fonts.paragraph, color: colors.greyLightTertiary, ...fonts.fontSize3 }}>
            {t("feed.feedSkip.plannedSkip", {defaultValue: "planned skip"})}: {selectedDateTime}
          </p>
          <Button 
            content={`${t("buttons.save",{defaultValue:"save"})} ${t("feed.feedSkip.plannedSkip", {defaultValue: "planned skip"})}`}       
            // onClick={} TODO: gem til db og luk modal
          />
        </div>
      }
    />
  )
}